<template>
  <div :class="$style['item']">
    <NuxtLink :to="productLink" :class="$style['item__image']" tabindex="-1" aria-hidden="true">
      <img
        v-if="item.imageFileId"
        :src="`${config.public.cdnUrl}/products/medium/${item.imageFileId}.webp`"
        alt=""
        loading="lazy">
    </NuxtLink>

    <div :class="$style['item__info']">
      <NuxtLink :to="productLink" :class="$style['item__title']">
        {{ item.title }}
      </NuxtLink>
      <span v-if="status" :class="[$style['item__status'], short && $style['item__status--short']]">
        <Icon :name="short ? 'AlertTriangle' : 'Check'" />
        {{ status }}
      </span>
    </div>

    <div :class="$style['item__actions']">
      <FormStepper
        :model-value="item.quantity"
        :label="`Aantal ${item.title}`"
        hide-label
        :max="max"
        @update:model-value="$emit('update-quantity', $event)" />

      <Button
        icon="Trash2"
        icon-only
        variant="ghost"
        type="button"
        :aria-label="`Verwijder ${item.title}`"
        @click="$emit('remove')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BagItem } from '~/composables/useShoppingBag'

// One item in the reservation: photo, title, availability in the chosen period, how many, remove
interface Availability {
  total: number
  available: number
}

const props = defineProps<{
  item: BagItem
  availability?: Availability | null
}>()

defineEmits<{
  remove: []
  'update-quantity': [qty: number]
}>()

const config = useRuntimeConfig()

const productLink = computed(() => ({ name: 'kleding-product-slug-id', params: { slug: props.item.slug, id: props.item.id } }))

// Never let the stepper go past what is free in the period, or what the revue has at all
const max = computed(() => Math.max(props.item.quantity, props.availability?.available ?? props.item.stock ?? 99))

const short = computed(() => !!props.availability && props.availability.available < props.item.quantity)

const status = computed(() => {
  const availability = props.availability
  if (!availability) { return '' }
  if (availability.available === 0) { return 'Niet vrij in deze periode' }
  if (availability.available < props.item.quantity) {
    return `Nog ${availability.available} vrij in deze periode`
  }
  return 'Vrij in deze periode'
})
</script>

<style module>
.item {
  display: grid;
  grid-template-columns: 4.5rem minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--spacing-l);
  padding: var(--spacing-l) 0;
  border-top: var(--stage-hairline) solid var(--stage-line);
}

.item__image {
  display: block;
  overflow: hidden;
  aspect-ratio: 3 / 4;
  background: var(--background-raised);
}

.item__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item__info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
  min-width: 0;
}

/* A product name, like on the cards: the body font, not a heading */
.item__title {
  color: var(--text-base);
  font-size: var(--font-size-base);
  line-height: 1.35;
  text-decoration: none;
}

.item__title:hover {
  color: var(--primary-color);
}

.item__status {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-s);
  color: var(--text-subtle);
  font-size: var(--font-size-small);
}

.item__status svg {
  flex: none;
  width: 1rem;
  height: 1rem;
}

.item__status--short {
  color: var(--stage-error);
  font-weight: var(--font-weight-bold);
}

.item__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing);
}

/* In a narrow column (the summary, a phone): the name at the top, the buttons at the bottom, both next to the photo */
@container (width <= 30rem) {
  .item {
    grid-template-columns: 4.5rem minmax(0, 1fr);
    grid-template-rows: auto auto;
    align-items: start;
    row-gap: var(--spacing-m);
  }

  .item__image {
    grid-row: span 2;
  }

  .item__actions {
    grid-column: 2;
    align-self: end;
  }
}
</style>
